import configPreferences from "csi/config/preferences";
import UserDefault from "csi/core/UserDefault";
import ExtendedPath from "csi/core/ExtendedPath";
import csi from "csi/main";
import reflection from "csi/reflection";
import sessionConstants from "csi/sessionConstants";
import { C8Node, cnl } from "csi/typings";
import xml from "csi/xml";
import { parseExtendedPath } from "csi/core/extendedPathService";

// TODO - eventually move to server? only valid for Supplier portal users though
const SUPPLIER_USER_OMIT_PATHS = [
	"(BaseProductSource)Child:__Parent__(FoodSourcing)",
	"(BaseProductSource)Child:__Parent__(FormulatedMaterialSourcing)",
	"(BaseProductSource)Child:__Parent__(StyleSourcing)",
	"(Colorway)Child:Style(Style)",
	"(ProductColor)Child:__Parent__(Product)",
	"(SRLineItem)Child:SRLineItemProduct(DataPackageContainer)",
	"(SupplierItemRevision)Child:QuoteProductAlternative(ProductAlternative)",
	"(SupplierRequest)Child:SRTemplate(SupplierRequestTemplate)",
	"(WbsHolder)Child:WBS(Wbs)",
];

/** create a UserDefault from a node */
function createUserDefault(node: C8Node, mapUrlToName: Record<cnl, string>): UserDefault {
	const {
		$Name,
		AllowBlanks,
		DefaultAttribute,
		Enforced,
		SupersededBy,
		TargetAttributeId,
		TargetAttributePath,
		TargetClass,
	} = node;

	let ep: ExtendedPath;

	if (TargetAttributePath) {
		ep = ExtendedPath.create(TargetClass, TargetAttributePath);
		if (!ep) {
			// if path is not valid, then don't process
			return null;
		}
	}

	// do one final check that target attribute exists before applying
	const rc = ep?.getEndClass() || reflection.getClass(TargetClass);
	const rca = reflection.getClassAttribute(rc, TargetAttributeId);
	if (!rca) {
		return null;
	}

	// map superseded urls to names
	const superseded = SupersededBy.map((url: cnl) => mapUrlToName[url]);

	const ud = new UserDefault(
		$Name,
		DefaultAttribute,
		TargetClass,
		TargetAttributeId,
		ep,
		xml.attributeTypeIsContainer(rca.valueType),
		Enforced,
		AllowBlanks,
		superseded,
	);

	configPreferences.addUserDefault(ud);
}

/** load reflection model */
export async function init(): Promise<void> {
	const fileUrl = csi.urlForGenerated("ReflectionModel.js", true);
	if (!fileUrl) {
		return Promise.reject();
	}

	const timeString = "Performance - loading reflection";
	console.time(timeString);

	try {
		// wait for reflection model to load
		await csi.loadScript(fileUrl);

		// populate HiddenPaths with contents of OmitPath tags in XML
		const { isSupplierUser, userDefaultNodes } = sessionConstants;

		if (isSupplierUser) {
			omitSupplierPaths();
		}

		// process user defaults
		if (userDefaultNodes?.length) {
			const hash = csi.hashArray(userDefaultNodes, "$URL", "$Name");
			userDefaultNodes.forEach((ud) => createUserDefault(ud, hash));
		}
	} finally {
		console.timeEnd(timeString);
	}
}

/** omit paths that are not meant to apply to Suppliers */
export function omitSupplierPaths(): void {
	SUPPLIER_USER_OMIT_PATHS.forEach((path) => {
		try {
			const ep = parseExtendedPath(path);
			configPreferences.addHiddenPath(ep);
		} catch (ex) {
			// nothing
		}
	});
}
